// Minimal in-memory send outbox for chat messages. The point is to
// generate a stable `clientMessageId` per send attempt so the backend
// can dedup a duplicate retry after a socket drop (see
// MessageIdempotencyService), and to GUARANTEE that every send promise
// eventually settles — so the caller's optimistic placeholder always
// reaches a terminal state (sent/failed) and can never hang on "sending".
//
// Scope is intentionally small: in-memory only, no persistence across
// page reload. A reload still loses pending sends — fixing that needs
// IndexedDB and is out of scope for the reconnect hardening pass.
//
// Usage:
//   const id = generateClientMessageId();
//   await sendWithRetry(id, () => providers.MessagesProvider.create(...));

import { onNetworkRecovery } from "./networkRecovery";

interface PendingSend {
  clientMessageId: string;
  attempt: number;
  inFlight: boolean;
  settled: boolean;
  lastError?: unknown;
  retryTimer: ReturnType<typeof setTimeout> | null;
  deadlineTimer: ReturnType<typeof setTimeout> | null;
  send: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (err: unknown) => void;
}

const MAX_ATTEMPTS = 3;
// Backoff before the 2nd and 3rd attempts. Retrying on a timer (in addition
// to the network-recovery hook) means recovery never depends solely on an
// online→offline→online transition firing — the classic case where a lost
// response left the promise pending forever.
const RETRY_BACKOFF_MS = [1000, 3000];
// Absolute backstop: even if the underlying request promise never settles
// (a truly hung socket), the outer promise rejects after this so the caller
// marks the placeholder failed instead of leaving it stuck on "sending".
const HARD_DEADLINE_MS = 30000;
const pending = new Map<string, PendingSend>();

function uuidv4(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // RFC4122 v4 fallback for older WebViews
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function generateClientMessageId(): string {
  return uuidv4();
}

function cleanup(entry: PendingSend) {
  if (entry.retryTimer) {
    clearTimeout(entry.retryTimer);
    entry.retryTimer = null;
  }
  if (entry.deadlineTimer) {
    clearTimeout(entry.deadlineTimer);
    entry.deadlineTimer = null;
  }
  pending.delete(entry.clientMessageId);
}

function settleResolve(entry: PendingSend, value: unknown) {
  if (entry.settled) return;
  entry.settled = true;
  cleanup(entry);
  entry.resolve(value);
}

function settleReject(entry: PendingSend, err: unknown) {
  if (entry.settled) return;
  entry.settled = true;
  cleanup(entry);
  entry.reject(err);
}

async function tryFlush(entry: PendingSend) {
  if (entry.settled || entry.inFlight) return;
  if (entry.retryTimer) {
    clearTimeout(entry.retryTimer);
    entry.retryTimer = null;
  }
  entry.inFlight = true;
  entry.attempt += 1;
  try {
    const result = await entry.send();
    settleResolve(entry, result);
  } catch (err) {
    entry.inFlight = false;
    entry.lastError = err;
    if (entry.attempt >= MAX_ATTEMPTS) {
      settleReject(entry, err);
      return;
    }
    // Schedule a bounded backoff retry. onNetworkRecovery may fire one sooner.
    const delay = RETRY_BACKOFF_MS[Math.min(entry.attempt - 1, RETRY_BACKOFF_MS.length - 1)];
    entry.retryTimer = setTimeout(() => {
      entry.retryTimer = null;
      void tryFlush(entry);
    }, delay);
  }
}

export function sendWithRetry<T>(clientMessageId: string, send: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const entry: PendingSend = {
      clientMessageId,
      attempt: 0,
      inFlight: false,
      settled: false,
      retryTimer: null,
      deadlineTimer: null,
      send: send as () => Promise<unknown>,
      resolve: resolve as (value: unknown) => void,
      reject,
    };
    // Guarantee the promise settles even if every attempt hangs.
    entry.deadlineTimer = setTimeout(() => {
      settleReject(entry, entry.lastError ?? new Error("message send timed out"));
    }, HARD_DEADLINE_MS);
    pending.set(clientMessageId, entry);
    void tryFlush(entry);
  });
}

// On network recovery, retry every still-pending send. The same
// clientMessageId is reused so server-side dedup drops the duplicate if the
// original actually made it through and only the response was lost.
onNetworkRecovery(() => {
  for (const entry of pending.values()) {
    if (!entry.settled && !entry.inFlight && entry.attempt < MAX_ATTEMPTS) void tryFlush(entry);
  }
});

export function pendingOutboxSize(): number {
  return pending.size;
}
